import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { LayoutDashboard, PhoneCall, Users } from 'lucide-react';

const supportPoints = [
  {
    icon: LayoutDashboard,
    title: 'Your Own Client Portal',
    description: "Every client gets access to LOLCorp's internal project & client management portal - track progress, share files, and message us directly, without waiting on an email thread.",
  },
  {
    icon: PhoneCall,
    title: 'A Direct Line, Even Off-Hours',
    description: "For genuinely urgent issues, the portal is plugged directly into our personal phones for real-time alerts - so a critical problem doesn't sit in an inbox until Monday.",
  },
  {
    icon: Users,
    title: 'Delivery Never Depends on One Person',
    description: "If Jack or Ryan are ever unreachable, a team of 18 vetted contractors is ready to step in and keep your project moving - so your delivery date never rests on a single person's availability.",
  },
];

export const SupportSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section className="py-24 md:py-32 bg-[#003333]" data-testid="support-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 md:mb-20"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-4">
            How We Support You
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white max-w-3xl mx-auto">
            Always Reachable, Never Dependent On One Person
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {supportPoints.map((point, index) => {
            const Icon = point.icon;
            return (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="bg-white/5 border border-white/10 hover:border-white/25 rounded-2xl p-7 transition-all duration-500"
                data-testid={`support-point-${index}`}
              >
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center text-white mb-6">
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-3">
                  {point.title}
                </h3>
                <p className="text-sm leading-relaxed text-white/60">
                  {point.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
